import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, ElementRef, HostBinding, inject, Injector, input, signal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IDType } from '@core/models/types';
import { ExecutionReferentialEntity, ReferentialsController } from '@core/referentials';
import { AppEnumService } from '@core/services';
import { isKindOfColumn, isReportEntity, isSemanticEntity } from '@entities/helpers';
import { BaseMixinComponent } from '@mixins/components/base-component';
import { TranslatableComponentMixin } from '@mixins/components/translatable';
import { TranslateModule } from '@ngx-translate/core';
import { BooleanInputType, toBooleanProperty } from '@utils/boolean';
import { stopAndPreventEvent } from '@utils/dom/event';
import { isNullishString } from '@utils/string';
import { MirIcon } from '@widgets/mir-icon';
import { TooltipDirective } from '@widgets/tooltip';
import { WizardItemEntity } from '@wizard/wizard-panel/models';
import { WizardPanelController } from '@wizard/wizard-panel/services';

const WIZARD_UI_TRAD_KEY = 'Wizard.Panel.UI';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'mir-wizard-panel-list-item',
  templateUrl: './wizard-panel-list-item.component.html',
  imports: [
    CommonModule,
    TranslateModule,
    MirIcon,
    TooltipDirective,
  ]
})
export class WizardPanelListItemComponent extends TranslatableComponentMixin(BaseMixinComponent) {

  @HostBinding('class')
  get hostCssClass(): string {
    return 'w-full h-fit flex flex-col shadow-1';
  }

  /** @Input */
  readonly entity = input.required<WizardItemEntity>();
  readonly editable = input(true, { transform: (value: BooleanInputType) => toBooleanProperty(value) });

  /** @Inject */
  readonly #elementRef = inject(ElementRef);
  readonly #referentialsController = inject(ReferentialsController);
  readonly #wizardPanelController = inject(WizardPanelController);
  readonly #appEnumService = inject(AppEnumService);

  protected readonly isSaving: Signal<boolean>;
  protected readonly entityColumns = signal<WizardItemEntity[]>([]);
  protected readonly isPartialEntity = signal(false);
  protected readonly executionReferentialTooltip = signal('❔');

  constructor() {
    const injector = inject(Injector);

    super();

    this.isSaving = toSignal(
      this.#wizardPanelController.isSaving$,
      { initialValue: false, injector }
    );

    effect(() => {
      const entity = this.entity();
      const entityColumns = entity?.items || [];
      const isPartialEntity = entityColumns.length > 0;
      const referentialId = entity.referentialId as IDType;
      const versionId = entity.versionId as IDType;

      this.entityColumns.set(entityColumns);
      this.isPartialEntity.set(isPartialEntity);
      this.initialiseTooltip({ referentialId, versionId });
    });
  }

  handleDeleteItem(event: Event, item: WizardItemEntity) {
    stopAndPreventEvent(event);
    this.#wizardPanelController.removeItem(item);
  }

  handleCommentAction(event: Event, item: WizardItemEntity) {
    stopAndPreventEvent(event);

    this.#wizardPanelController.openCommentEditor({
      item,
      elementRef: this.#elementRef,
      action: 'Edit'
    });
  }

  itemHasComment(item: WizardItemEntity): boolean {
    return !isNullishString(item.comment);
  }

  /** 🧰 cache tooltip ? */
  columnItemCommentTooltip(item: WizardItemEntity): string {
    const tradKey = this.itemHasComment(item)
      ? 'ModifyComment'
      : 'AddComment';
    return this.translate(`${WIZARD_UI_TRAD_KEY}.Actions.${tradKey}`);
  }

  getItemLabel(item: WizardItemEntity): string {
    return isReportEntity(item) || isSemanticEntity(item) || isKindOfColumn(item)
      ? (item.functionalName ?? item.technicalName ?? item.label)
      : (item.technicalName ?? item.functionalName ?? item.label);
  }

  private initialiseTooltip(executionReferential: ExecutionReferentialEntity) {
    const entity = this.entity();

    if (entity) {
      const { referentialId, versionId } = executionReferential;
      const referentialName = this.#referentialsController.getCompanyReferentialLocalName(referentialId);
      const referential = this.#referentialsController.getReferentialById(referentialId);
      const versionName = referential.versions.find(version => version.id === versionId)?.name;
      const platform = (entity.platform === 'Unknown')
        ? undefined
        : this.#appEnumService.getEnumEntry('platform', entity.platform);
      const subType = this.#appEnumService.getEnumEntry('subType', entity.subType);

      this.executionReferentialTooltip.set(
        createHtmlExecutionReferentialTooltip(referentialName, versionName, platform, subType)
      );
    }

  }
}

const _execution_group_html_div_start = `<div class="inline-flex justify-start items-center gap-x-[1ch] text-sm">` as const;
const _execution_referential_line_label_html_div_start = `<div class="font-medium text-gray-900 mr-1">` as const;
const _execution_referential_light_label_html_div_start = `<div class="font-normal text-gray-600">` as const;
const _execution_row_div = `<div class="flex flex-col>` as const;
const _execution_html_div_end = `</div>` as const;

export function createHtmlExecutionReferentialTooltip(referentialName: string, versionName: string, platform: string, subType: string): string {
  let tooltip = `${_execution_row_div}
      ${_execution_group_html_div_start}
        ${_execution_referential_line_label_html_div_start}
          ${referentialName}
        ${_execution_html_div_end}
        ${_execution_referential_light_label_html_div_start}
          ${versionName}
        ${_execution_html_div_end}
      ${_execution_html_div_end}
    ${_execution_html_div_end}`;

  if (!isNullishString(subType)) {
    const platformNode = platform
      ? `${_execution_referential_line_label_html_div_start}
            ${platform}
          ${_execution_html_div_end}`
      : '';
    tooltip = `${tooltip}
      ${_execution_row_div}
        ${_execution_group_html_div_start}
          ${platformNode}
          ${_execution_referential_light_label_html_div_start}
            ${subType}
          ${_execution_html_div_end}
        ${_execution_html_div_end}
      ${_execution_html_div_end}`;
  }

  return tooltip;
}
